import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import FeedbackReplyPopup from '../../../../../components/FeedbackReplyPopup'
import IPCheckBox from '../../../../../components/IPCheckBox'
import IPModal from '../../../../../components/IPModal'
import IPRadio from '../../../../../components/IPRadio'
import IPTextarea from '../../../../../components/IPTextarea'
import { adminFeedbackReasonLabel } from '../feedbackReasons'
import {
  buildResolveRequest,
  htmlVisibleText,
  initialResolveForm,
  INTERNAL_NOTE_MAX_LENGTH,
  NO_ANNOUNCE_PERMISSION_TEXT,
  POPUP_HTML_MAX_LENGTH,
  POPUP_TITLE_MAX_LENGTH,
  RESOLUTION_STATUS_OPTIONS,
  resolveErrorMessage,
  ResolveFormState,
  UNANNOUNCED_CONFIRM_TEXT,
  validateResolveForm,
} from '../feedbackResolution'
import { IChatFeedbackRow, IFeedbackResolveRequest, IFeedbackResolveResponse } from '../models'
import { formatCount } from '../viewModel'
import styles from './ChatbotAnalytics.module.scss'

// CKEditor paketi büyüktür; yalnız modal açılınca yüklenir.
const PopupHtmlEditor = lazy(() => import('./PopupHtmlEditor'))

const DASH = '—'

export type ResolveFeedbackAction = (
  feedbackId: number,
  request: IFeedbackResolveRequest
) => Promise<IFeedbackResolveResponse>

export interface FeedbackResolveModalProps {
  row: IChatFeedbackRow
  /** Oturumdaki admin bayiye duyurabilir mi; sunucu aynı kontrolü bağımsız yapar (1403). */
  canAnnounce: boolean
  resolveFeedback: ResolveFeedbackAction
  onCancel: () => void
  onResolved: () => void
}

/** Olumsuz geri bildirimi Geliştirildi / Kapatıldı olarak sonuçlandırma ekranı (Faz-37 §2). */
export default function FeedbackResolveModal({
  row,
  canAnnounce,
  resolveFeedback,
  onCancel,
  onResolved,
}: FeedbackResolveModalProps) {
  const [form, setForm] = useState<ResolveFormState>(() => initialResolveForm(canAnnounce))
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const update = (patch: Partial<ResolveFormState>) => {
    setForm(current => ({ ...current, ...patch }))
    setError(null)
  }

  const save = async () => {
    if (saving) return
    const validation = validateResolveForm(form, canAnnounce)
    if (validation) {
      setError(validation)
      return
    }

    if (!form.announce) {
      const answer = await Swal.fire({
        icon: 'warning',
        title: 'Duyurusuz kapatma',
        text: UNANNOUNCED_CONFIRM_TEXT,
        showCancelButton: true,
        confirmButtonText: 'Evet',
        cancelButtonText: 'Hayır',
        allowOutsideClick: false,
      })
      if (!answer.isConfirmed) return
    }

    setSaving(true)
    setError(null)
    try {
      await resolveFeedback(row.FeedbackId, buildResolveRequest(form))
      if (mountedRef.current) onResolved()
    } catch (resolveError) {
      if (mountedRef.current) setError(resolveErrorMessage(resolveError))
    } finally {
      if (mountedRef.current) setSaving(false)
    }
  }

  /** Bayinin göreceği popup'ın aynısı (aynı bileşen); başlık ve içerik olmadan açılmaz. */
  const openPreview = () => {
    if (!form.popupTitle.trim() || !htmlVisibleText(form.popupHtml)) {
      setError('Önizleme için popup başlığı ve içeriği girin.')
      return
    }
    setPreviewOpen(true)
  }

  return (
    <>
      <IPModal
        show
        title='Geri bildirimi sonuçlandır'
        width={760}
        confirmText={saving ? 'Kaydediliyor…' : 'Kaydet'}
        onConfirm={save}
        onClose={onCancel}
        bodyOverflow
      >
        <div className={styles.resolveForm} data-testid='resolve-modal'>
          <dl className={styles.resolveContext}>
            <div>
              <dt>Bayi</dt>
              <dd>
                {row.DealerName ?? DASH}
                <small className={styles.dealerCode}> {row.DealerSapCode}</small>
              </dd>
            </div>
            <div>
              <dt>Neden</dt>
              <dd>{adminFeedbackReasonLabel(row.ReasonCode)}</dd>
            </div>
            <div>
              <dt>Yorum</dt>
              <dd>{row.Comment ?? DASH}</dd>
            </div>
          </dl>

          <fieldset className={styles.resolveField}>
            <legend>Statü</legend>
            <div className={styles.resolveRadios} role='radiogroup' aria-label='Statü'>
              {RESOLUTION_STATUS_OPTIONS.map(option => (
                <IPRadio
                  key={option.value}
                  name='feedback-resolution-status'
                  text={option.label}
                  value={option.value}
                  data-testid={`resolve-status-${option.value}`}
                  checked={form.status === option.value}
                  onChange={() => update({ status: option.value })}
                />
              ))}
            </div>
          </fieldset>

          <div className={styles.resolveField}>
            <IPCheckBox
              id='resolve-announce'
              label='Bayiye duyur (giriş sonrası popup)'
              checked={form.announce}
              disabled={!canAnnounce}
              removeMarginBottom
              onCheckedChange={checked => update({ announce: checked })}
            />
            {!canAnnounce ? (
              <small className={styles.resolveHint} data-testid='resolve-no-permission'>
                {NO_ANNOUNCE_PERMISSION_TEXT}
              </small>
            ) : null}
          </div>

          {form.announce ? (
            <>
              <div className={styles.resolveField}>
                <label htmlFor='resolve-popup-title'>Popup başlığı</label>
                <input
                  id='resolve-popup-title'
                  type='text'
                  className={styles.resolveInput}
                  value={form.popupTitle}
                  maxLength={POPUP_TITLE_MAX_LENGTH}
                  onChange={event => update({ popupTitle: event.target.value })}
                />
                <small className={styles.resolveCounter}>
                  {formatCount(form.popupTitle.length)} / {formatCount(POPUP_TITLE_MAX_LENGTH)}
                </small>
              </div>

              <div className={styles.resolveField}>
                <span className={styles.resolveLabel}>Popup içeriği</span>
                <div className={styles.resolveEditor}>
                  <Suspense fallback={<p className={styles.resolveHint}>Editör yükleniyor…</p>}>
                    <PopupHtmlEditor value={form.popupHtml} onChange={html => update({ popupHtml: html })} />
                  </Suspense>
                </div>
                <div className={styles.resolveEditorFooter}>
                  <small
                    className={
                      form.popupHtml.length > POPUP_HTML_MAX_LENGTH
                        ? `${styles.resolveCounter} ${styles.resolveCounterOver}`
                        : styles.resolveCounter
                    }
                    data-testid='resolve-html-counter'
                  >
                    {formatCount(form.popupHtml.length)} / {formatCount(POPUP_HTML_MAX_LENGTH)} karakter (HTML)
                  </small>
                  <button
                    type='button'
                    className={styles.secondaryButton}
                    data-testid='resolve-preview'
                    onClick={openPreview}
                  >
                    Önizle
                  </button>
                </div>
              </div>
            </>
          ) : null}

          <div className={styles.resolveField}>
            <label htmlFor='resolve-internal-note'>İç not (isteğe bağlı)</label>
            <IPTextarea
              id='resolve-internal-note'
              rows={3}
              value={form.internalNote}
              maxLength={INTERNAL_NOTE_MAX_LENGTH}
              onChange={event => update({ internalNote: event.target.value })}
            />
            <small className={styles.resolveHint}>
              Yalnız Tamamlananlar&apos;da görünür, bayiye gitmez. {formatCount(form.internalNote.length)} /{' '}
              {formatCount(INTERNAL_NOTE_MAX_LENGTH)}
            </small>
          </div>

          {error ? (
            <div className={styles.resolveError} role='alert' data-testid='resolve-error'>
              {error}
            </div>
          ) : null}
        </div>
      </IPModal>

      {previewOpen ? (
        <FeedbackReplyPopup
          title={form.popupTitle.trim()}
          html={form.popupHtml}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </>
  )
}
